import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import InternalApproverPagedCriteria from '../Models/Approvals/Lookups/InternalApproverPagedCriteria';
import InternalApproverLookup from '../Models/Approvals/Lookups/InternalApproverLookup';
import InternalApprover from '../Models/Approvals/InternalApprover';
import InternalApprovalEntity from '../Models/Approvals/InternalApprovalEntity';

export interface IInternalApproverApiClient {

    /**
     * Gets a paged list of approver lookups
     * @param pageRequest The filter criteria
     * @returns A list of
     */
    getInternalApproverPaged(pageRequest: Model.PartialPlainObject<Data.PageRequest<InternalApproverPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<InternalApproverLookup>>>;

    /**
     * Gets a list of approver lookups
     */
    getInternalApproverLookups(): AxiosPromise<Array<Model.PlainObject<InternalApproverLookup>>>;

    /**
     * Gets an approver with the specified id
     */
    getInternalApprover(internalApproverId: number): AxiosPromise<Model.PlainTrackedObject<InternalApprover>>;

    /**
     * Creates a new approver
     */
    save(internalApprover: Model.PartialPlainObject<InternalApprover>): AxiosPromise;

    /**
     * Deletes the approver with the given id
     */
    delete(internalApproverId: number): AxiosPromise;

    getInternalApprovalEntities(): AxiosPromise<Array<Model.PlainTrackedObject<InternalApprovalEntity>>>;

    saveInternalApprovalEntities(entities: Array<Model.PartialPlainObject<InternalApprovalEntity>>): AxiosPromise<Array<Model.PlainTrackedObject<InternalApprovalEntity>>>;

    // Client only properties / methods
}

@injectable()
export default class InternalApproverApiClient extends Data.ApiClientBase implements IInternalApproverApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/internal-approvers`);
    }

    public getInternalApproverPaged(pageRequest: Model.PartialPlainObject<Data.PageRequest<InternalApproverPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<InternalApproverLookup>>> {
        return this.axios.post(`${this.apiPath}/paged`, pageRequest);
    }

    public getInternalApproverLookups(): AxiosPromise<Array<Model.PlainObject<InternalApproverLookup>>> {
        return this.axios.get(`${this.apiPath}/lookups`);
    }

    public getInternalApprover(internalApproverId: number): AxiosPromise<Model.PlainTrackedObject<InternalApprover>> {
        return this.axios.get(`${this.apiPath}/${internalApproverId}`);
    }

    public save(internalApprover: Model.PartialPlainObject<InternalApprover>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/create`, internalApprover);
    }

    public delete(internalApproverId: number): AxiosPromise {
        return this.axios.delete(`${this.apiPath}/${internalApproverId}`);
    }

    public getInternalApprovalEntities(): AxiosPromise<Array<Model.PlainTrackedObject<InternalApprovalEntity>>> {
        return this.axios.get(`${this.apiPath}/internal-approval-entities`);
    }

    public saveInternalApprovalEntities(entities: Array<Model.PartialPlainObject<InternalApprovalEntity>>): AxiosPromise<Array<Model.PlainTrackedObject<InternalApprovalEntity>>> {
        return this.axios.post(`${this.apiPath}/internal-approval-entities`, entities);
    }

    // Client only properties / methods
}